/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import Modal from "@mui/material/Modal";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import useYupValidationResolver from "utils/yup/useYupValidationResolver";
import * as Yup from "yup";
import typography from "assets/theme/base/typography";
import colors from "assets/theme/base/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment/moment";
import { getWeekdayOccurrence } from "utils/Date";

const { size } = typography;
const formLabelStyle = { fontSize: size.md };

const recurrenceOptions = [
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
];

const repeatOnOptions = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function CheckedIcon({ text }) {
  return (
    <span
      style={{
        fontSize: size.xs,
        borderRadius: "50%",
        width: 10,
        height: 10,
        padding: 5,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {text.charAt(0).toUpperCase()}
    </span>
  );
}

const defaultRepeatEveryMonthOptions = ["byDate", "byWeekDay"];

function CustomRepeatIntervalModal({
  open,
  handleClose,
  isLoading,
  startDateV,
  timezoneV,
  setFieldValue,
  values,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 400 ? "100%" : 400,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    boxShadow: 24,
    p: 4,
    outline: "none",
    borderRadius: "0.5rem",
  };

  const [initialValues] = useState({
    repeatEveryCount: 1,
    repeatEveryType: "day",
    repeatOn: "",
    repeatEnds: "never",
    repeatEndsOn: moment.tz(startDateV, timezoneV).endOf("month"),
    occurrences: 1,
  });

  const [repeatEveryMonthOptions, setRepeatEveryMonthOptions] = useState(
    defaultRepeatEveryMonthOptions
  );

  const validationSchema = Yup.object({
    repeatEveryCount: Yup.number()
      .nullable()
      .positive("Repeat count must be a greater than zero")
      .required("Repeat count is required"),
    repeatEveryType: Yup.string().required("Repeat type is required"),
    repeatOn: Yup.string().when("repeatEveryType", {
      is: (value) => ["week", "month"].includes(value),
      then: Yup.string().required("Repeat On is required"),
    }),
    repeatEnds: Yup.string().required("Repeat End is required"),
    repeatEndsOn: Yup.string().when("repeatEnds", {
      is: "on",
      then: Yup.string().required("Repeat On is required"),
    }),
    occurrences: Yup.number().when("repeatEnds", {
      is: "after",
      then: Yup.number()
        .nullable()
        .positive("Occurrences must be a greater than zero")
        .required("Occurrences is required"),
    }),
  });

  const resolver = useYupValidationResolver(validationSchema);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver,
    reValidateMode: "all",
    mode: "all",
    defaultValues: {
      repeatEveryCount: values?.repeatEveryCount || initialValues.repeatEveryCount,
      repeatEveryType: values?.repeatEveryType || initialValues.repeatEveryType,
      repeatOn: values?.repeatOn || initialValues.repeatOn,
      repeatEnds: values?.repeatEnds || initialValues.repeatEnds,
      repeatEndsOn: values?.repeatEndsOn || initialValues.repeatEndsOn,
      occurrences: values?.occurrences || initialValues.occurrences,
    },
  });
  const watchRepeatEveryCount = watch("repeatEveryCount", 1);
  const watchRepeatEveryType = watch("repeatEveryType", "day");

  const onSubmit = async (data) => {
    if (
      (data?.repeatEnds === "on" &&
        (!data?.repeatEndsOn || !moment(data?.repeatEndsOn).isValid())) ||
      (data?.repeatEnds === "after" && !data?.occurrences)
    )
      return;

    setFieldValue("duration.customRepeatInterval", {
      ...data,
      repeatEndsOn:
        data?.repeatEndsOn === "Invalid date"
          ? null
          : moment(data?.repeatEndsOn)?.format("DD-MM-YYYY HH:mm"),
    });
    handleClose();
  };

  // eslint-disable-next-line consistent-return
  const generateHelperTextForStartDate = () => {
    if (startDateV && errors?.duration?.startDate) {
      return errors?.duration?.startDate;
    }
    if (!startDateV && errors?.duration?.startDate) {
      return "Start Time is required";
    }
  };

  // eslint-disable-next-line consistent-return
  const startDateHasError = () => {
    if (errors?.duration?.startDate) {
      return true;
    }
    if (!startDateV && Boolean(errors?.duration?.startDate)) {
      return true;
    }
  };

  useEffect(() => {
    const date = moment.tz(startDateV, timezoneV);
    const day = date.format("dddd");
    const currentDate = date.format("DD");
    const lastDate = moment.tz(startDateV, timezoneV).endOf("month").format("DD");

    let count = 0;
    // eslint-disable-next-line no-plusplus
    for (let d = 1; d <= date.date(); d++) {
      if (moment.tz([date.year(), date.month(), d], timezoneV).format("dddd") === day) {
        // eslint-disable-next-line no-plusplus
        count++;
      }
    }
    if (count > 4) {
      setRepeatEveryMonthOptions(["byDate", "byLastWeekDay"]);
    } else if (lastDate - currentDate < 7) {
      setRepeatEveryMonthOptions([...defaultRepeatEveryMonthOptions, "byLastWeekDay"]);
    } else {
      setRepeatEveryMonthOptions(defaultRepeatEveryMonthOptions);
    }
  }, [startDateV]);

  return (
    <Modal
      open={open}
      onClose={() => {
        setFieldValue("duration.repeatInterval", "daily");
        handleClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <Typography variant="h5" mb={2}>
          Custom Repeat Interval
        </Typography>
        <form>
          <MDBox
            mt={2}
            sx={{
              "& .MuiFormHelperText-root": { color: "#d32f2f" },
            }}
          >
            <MDBox mb={2} sx={{ display: "flex" }}>
              <MDBox sx={{ flex: 1 }}>
                <FormLabel sx={formLabelStyle} id="repeat-every-label" mr={2}>
                  Repeat every
                </FormLabel>
              </MDBox>
              <MDBox aria-labelledby="repeat-every-label" sx={{ display: "flex", flex: 2 }}>
                <MDBox mx={2} sx={{ flex: 1 }}>
                  <MDInput
                    type="number"
                    variant="standard"
                    error={errors?.repeatEveryCount?.message}
                    {...register("repeatEveryCount")}
                    disabled={isLoading}
                    min={1}
                  />
                  {errors?.repeatEveryCount?.message ? (
                    <FormHelperText>{errors.repeatEveryCount.message}</FormHelperText>
                  ) : null}
                </MDBox>
                <MDBox mx={2} sx={{ flex: 2 }}>
                  <Controller
                    control={control}
                    name="repeatEveryType"
                    rules={{ required: true }}
                    disabled={isLoading}
                    render={({ field: { value } }) => (
                      <Autocomplete
                        fullWidth
                        autoComplete
                        disableClearable
                        includeInputInList
                        id="repeatEveryType"
                        value={value && recurrenceOptions.find((option) => option.value === value)}
                        onChange={(_, option) => {
                          if (option?.value === "week") {
                            setValue("repeatOn", `0111110`);
                          } else if (option?.value === "month") {
                            setValue("repeatOn", "byDate");
                          } else {
                            setValue("repeatOn", "");
                          }

                          if (["month", "year"].includes(option?.value)) {
                            setValue(
                              "repeatEndsOn",
                              moment.tz(startDateV, timezoneV).add(1, "year").endOf("month")
                            );
                          } else {
                            setValue(
                              "repeatEndsOn",
                              moment.tz(startDateV, timezoneV).endOf("month")
                            );
                          }
                          setValue("repeatEveryType", option?.value);
                        }}
                        options={recurrenceOptions}
                        getOptionLabel={(option) =>
                          `${option?.label}${watchRepeatEveryCount > 1 ? "s" : ""}` || ""
                        }
                        isOptionEqualToValue={(option, val) => option?.value === val?.value}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            error={errors?.repeatEveryType?.message}
                            helperText={errors?.repeatEveryType?.message}
                          />
                        )}
                        disabled={isLoading}
                      />
                    )}
                  />
                </MDBox>
              </MDBox>
            </MDBox>
            {watchRepeatEveryType === "week" ? (
              <MDBox mb={2}>
                <FormLabel sx={formLabelStyle} id="repeat-on-label" mr={2}>
                  Repeat on
                </FormLabel>
                <MDBox mb={2}>
                  {repeatOnOptions.map((repeatOnOption, index) => (
                    <Controller
                      key={`repeatOn.${repeatOnOption}`}
                      sx={{ margin: 2 }}
                      control={control}
                      name="repeatOn"
                      disabled={isLoading}
                      render={({ field: { value } }) => (
                        <Checkbox
                          id="repeatOn"
                          checked={value[index] === "1"}
                          onChange={(e) =>
                            setValue(
                              "repeatOn",
                              `${value.substr(0, index)}${
                                e.target.checked ? "1" : "0"
                              }${value.substr(index + 1)}`
                            )
                          }
                          icon={<CheckedIcon text={repeatOnOption} />}
                          checkedIcon={<CheckedIcon text={repeatOnOption} />}
                          sx={{
                            margin: "5px",
                            color: value[index] === "1" ? colors.textLight.main : colors.text.main,
                            "&.Mui-checked": {
                              color: colors.textLight.main,
                              backgroundColor: colors.gray80.main,
                            },
                          }}
                        />
                      )}
                    />
                  ))}
                </MDBox>
              </MDBox>
            ) : watchRepeatEveryType === "month" ? (
              <MDBox mb={2}>
                <Controller
                  control={control}
                  name="repeatOn"
                  rules={{ required: true }}
                  disabled={isLoading}
                  render={({ field: { value } }) => (
                    <Autocomplete
                      fullWidth
                      autoComplete
                      disableClearable
                      includeInputInList
                      id="repeatOn"
                      value={value}
                      onChange={(_, selectedValue) => setValue("repeatOn", selectedValue)}
                      options={repeatEveryMonthOptions}
                      getOptionLabel={(option) =>
                        option === "byDate"
                          ? `Monthly on day ${moment.tz(startDateV, timezoneV).format("D")}`
                          : `Monthly on the ${getWeekdayOccurrence(startDateV, timezoneV, option)}`
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          error={errors?.repeatOn?.message}
                          helperText={errors?.repeatOn?.message}
                        />
                      )}
                      disabled={isLoading}
                    />
                  )}
                />
              </MDBox>
            ) : null}
            <MDBox mb={2}>
              <FormLabel sx={formLabelStyle} id="ends-radio-buttons-group-label" mr={2}>
                Ends
              </FormLabel>
              <Controller
                control={control}
                name="repeatEnds"
                disabled={isLoading}
                render={({ field: { value } }) => (
                  <RadioGroup
                    aria-labelledby="ends-radio-buttons-group-label"
                    name="repeatEnds"
                    value={value}
                    onChange={(e) => setValue("repeatEnds", e.target.value)}
                  >
                    <FormControlLabel value="never" control={<Radio />} label="Never" />
                    <FormControlLabel
                      value="on"
                      control={<Radio />}
                      label={
                        <MDBox
                          my={1}
                          sx={{ display: "flex", alignItems: "center", justifyContent: "start" }}
                        >
                          On
                          <Controller
                            control={control}
                            name="repeatEndsOn"
                            disabled={isLoading}
                            render={({ field: { value: nestedValue } }) => (
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  disabled={value !== "on"}
                                  sx={{ marginLeft: 4 }}
                                  disablePast
                                  value={nestedValue}
                                  onChange={(newValue) => {
                                    setValue("repeatEndsOn", newValue);
                                  }}
                                  slotProps={{
                                    textField: {
                                      variant: "standard",
                                      fullWidth: true,
                                      error: startDateHasError(),
                                      helperText: generateHelperTextForStartDate(),
                                    },
                                  }}
                                  viewRenderers={{
                                    hours: null,
                                    minutes: null,
                                    seconds: null,
                                  }}
                                />
                              </LocalizationProvider>
                            )}
                          />
                        </MDBox>
                      }
                    />
                    <FormControlLabel
                      value="after"
                      control={<Radio />}
                      label={
                        <MDBox
                          my={1}
                          sx={{ display: "flex", alignItems: "center", justifyContent: "start" }}
                        >
                          After
                          <MDBox sx={{ flex: 3 }}>
                            <MDInput
                              disabled={isLoading || value !== "after"}
                              size="small"
                              sx={{ marginLeft: 4, maxWidth: 200 }}
                              type="number"
                              variant="standard"
                              fullWidth={false}
                              error={errors?.occurrences?.message}
                              {...register("occurrences")}
                              min={1}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <p style={formLabelStyle}>occurrences</p>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </MDBox>
                        </MDBox>
                      }
                    />
                  </RadioGroup>
                )}
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                disabled={isLoading}
                color="primaryRed"
                fullWidth
                onClick={() => handleSubmit(onSubmit)()}
              >
                Save
              </MDButton>
            </MDBox>
          </MDBox>
        </form>
      </MDBox>
    </Modal>
  );
}

export default CustomRepeatIntervalModal;
